/**
 * Anonymous visitor-id + UTM-aware page-view beacon.
 *
 * Mounts once at the App root. On every route change:
 *   1. Ensures a stable `vid` in localStorage (8-byte UUID; survives
 *      refreshes + multiple tabs).
 *   2. Reads `?ref`, `?utm_source`, `?utm_medium`, `?utm_campaign`,
 *      `?utm_content`, `?utm_term` from the URL.
 *   3. Persists the first-touch attribution to localStorage so the
 *      eventual /register call can stamp it onto the user record.
 *   4. Fires POST /api/public/page-view as a beacon (non-blocking,
 *      sendBeacon when available, fetch keepalive fallback).
 *
 * Backend pairs this with /api/admin/metrics/attribution-funnel to
 * answer "where did our 36 visits actually come from?" — closes the
 * gap between Meta's ad dashboard and our own funnel.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const VID_KEY = "fh_vid";
const ATTR_KEY = "fh_first_attribution";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function ensureVid() {
  try {
    let vid = localStorage.getItem(VID_KEY);
    if (vid) return vid;
    // crypto.randomUUID isn't available on older Safari; fall back.
    vid = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(VID_KEY, vid);
    return vid;
  } catch {
    // localStorage blocked (private mode, embedded webviews). Best effort:
    // generate a one-shot id so the current pageview still attributes.
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/**
 * Parse `?utm_*` and `?ref` off the current URL. If anything is found,
 * cache it as the user's first-touch attribution (only stored once —
 * subsequent visits do not overwrite).
 */
function captureAttribution(search) {
  const params = new URLSearchParams(search || "");
  const fields = {
    ref: params.get("ref") || null,
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_content: params.get("utm_content") || null,
    utm_term: params.get("utm_term") || null,
  };
  // Persist first-touch only — first ad / first referral wins.
  try {
    const cached = localStorage.getItem(ATTR_KEY);
    if (!cached && Object.values(fields).some((v) => v)) {
      localStorage.setItem(ATTR_KEY, JSON.stringify(fields));
    }
  } catch {
    // ignore — beacon still includes the live URL params for this hit
  }
  return fields;
}

function sendBeacon(body) {
  const url = `${BACKEND_URL}/api/public/page-view`;
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
  } catch {
    // Some browsers THROW on blocked beacons instead of returning false
    // (old Chrome crbug/490015, strict privacy modes) — fall through to fetch.
  }
  // Fallback for browsers that refuse sendBeacon (often Brave) — keepalive
  // lets the request finish even if the user navigates away.
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Continuation-email arrivals carry `?lt=<signed lead token>`. We only tie
 * the lead to this browser after a FIRST-PARTY ENGAGED visit — 5s of
 * visible dwell or a real interaction — so link scanners and previews
 * never create an association. Fires once per token per session.
 */
function useLeadVisitBeacon(search, pathname) {
  useEffect(() => {
    let lt = "";
    try {
      lt = new URLSearchParams(search).get("lt") || "";
      if (lt) sessionStorage.setItem("lead_lt", lt);
      else lt = sessionStorage.getItem("lead_lt") || "";
    } catch {
      return undefined;
    }
    if (!lt) return undefined;
    const sentKey = `lead_visit_sent:${lt.slice(-24)}`;
    try {
      if (sessionStorage.getItem(sentKey)) return undefined;
    } catch {
      return undefined;
    }
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      try { sessionStorage.setItem(sentKey, "1"); } catch { /* ignore */ }
      fetch(`${BACKEND_URL}/api/public/lead-visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lt, vid: ensureVid(), path: pathname }),
        keepalive: true,
      }).catch(() => {});
      cleanup();
    };
    const timer = setTimeout(() => {
      if (document.visibilityState === "visible") fire();
    }, 5000);
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, fire, { passive: true, once: true }));
    function cleanup() {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, fire));
    }
    return cleanup;
  }, [search, pathname]);
}

export default function PageViewTracker() {
  const loc = useLocation();
  useLeadVisitBeacon(loc.search, loc.pathname);
  useEffect(() => {
    // Skip admin pages — they're for the operator, not the funnel.
    if (loc.pathname.startsWith("/app/admin")) return;
    const vid = ensureVid();
    const live = captureAttribution(loc.search);
    sendBeacon({
      vid,
      path: loc.pathname,
      ref: live.ref,
      utm_source: live.utm_source,
      utm_medium: live.utm_medium,
      utm_campaign: live.utm_campaign,
      utm_content: live.utm_content,
      utm_term: live.utm_term,
      // `referrer` = the previous origin the user came FROM. Crucial when
      // a click strips UTMs (organic shares, in-app browsers).
      referrer: typeof document !== "undefined" ? document.referrer : "",
    });
  }, [loc.pathname, loc.search]);
  return null;
}

/**
 * Read the cached anonymous visitor id (or generate one) for use by
 * the signup flow — lets the backend join the new user to their first
 * page view.
 */
export function readVid() {
  return ensureVid();
}

/**
 * Visitor id + cached attribution for the quiz submit body, so the backend can
 * stitch this completion to the later trial account (cohort report).
 */
export function quizIdentity() {
  try {
    let attr = {};
    try {
      attr = JSON.parse(localStorage.getItem(ATTR_KEY) || "{}");
    } catch {
      attr = {};
    }
    const params = new URLSearchParams(window.location.search);
    const clip = (v) => (v ? String(v).slice(0, 80) : null);
    return {
      vid: clip(ensureVid()),
      utm_source: clip(params.get("utm_source") || attr.utm_source),
      utm_campaign: clip(params.get("utm_campaign") || attr.utm_campaign),
    };
  } catch {
    return {};
  }
}

/**
 * Fire-and-forget quiz funnel beacon. Steps: start, q1, half, complete,
 * gate_shown, lead. Tracking must never break the quiz — everything is
 * wrapped and best-effort.
 */
export function sendQuizFunnelEvent(step, extra = {}) {
  try {
    const vid = ensureVid();
    let attr = {};
    try {
      attr = JSON.parse(localStorage.getItem(ATTR_KEY) || "{}");
    } catch {
      // no cached attribution — fall through to live URL params
    }
    const params = new URLSearchParams(window.location.search);
    const body = {
      vid,
      step,
      ref: params.get("ref") || attr.ref || null,
      utm_source: params.get("utm_source") || attr.utm_source || null,
      utm_campaign: params.get("utm_campaign") || attr.utm_campaign || null,
      ...extra,
    };
    const url = `${BACKEND_URL}/api/public/quiz-funnel`;
    try {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    } catch {
      // throwing beacon (blocked/legacy) — fall through to fetch
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let tracking interfere with the quiz
  }
}

/**
 * Fire-and-forget quiz abandonment beacon — outcomes of the mid-quiz
 * exit-intent rescue modal. Actions: rescued, feedback (+reason),
 * dismissed. Same delivery strategy as the funnel beacon.
 */
export function sendQuizAbandonEvent(extra = {}) {
  try {
    const vid = ensureVid();
    let attr = {};
    try {
      attr = JSON.parse(localStorage.getItem(ATTR_KEY) || "{}");
    } catch {
      // no cached attribution — fall through to live URL params
    }
    const params = new URLSearchParams(window.location.search);
    const body = {
      vid,
      ref: params.get("ref") || attr.ref || null,
      utm_source: params.get("utm_source") || attr.utm_source || null,
      ...extra,
    };
    const url = `${BACKEND_URL}/api/public/quiz-abandon`;
    try {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    } catch {
      // throwing beacon (blocked/legacy) — fall through to fetch
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let tracking interfere with the quiz
  }
}

/**
 * Fire-and-forget video watch beacon (hero teaser). Events: play,
 * watch_10s, watch_30s, modal_open. One-shot guarding is the caller's
 * job — this just ships the beacon.
 */
export function sendVideoEvent(event, video = "hero_teaser") {
  try {
    const vid = ensureVid();
    let attr = {};
    try {
      attr = JSON.parse(localStorage.getItem(ATTR_KEY) || "{}");
    } catch {
      // no cached attribution — fall through to live URL params
    }
    const params = new URLSearchParams(window.location.search);
    const body = {
      vid,
      event,
      video,
      ref: params.get("ref") || attr.ref || null,
    };
    const url = `${BACKEND_URL}/api/public/video-event`;
    try {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
    } catch {
      // throwing beacon (blocked/legacy) — fall through to fetch
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let tracking break the page
  }
}
